import { type PropsWithChildren, type ReactElement, type ReactNode } from 'react';

import { type IconComponent } from '@air/design-icons';
import { cn } from '@air/utils-tailwind';

import { EMPTY_BOARD } from '~/constants/testIDs';

type NullStateTile =
  | { icon: IconComponent; logo?: never }
  /** A pre-rendered element, e.g. a third-party brand logo that isn't a design-system icon. */
  | { icon?: never; logo: ReactElement };

export type NullStateProps = NullStateTile & {
  className?: string;
  /** Use a lower level when the null state sits inside a surface that already has its own `h1`, e.g. a popover. */
  headingAs?: 'h1' | 'h2' | 'h3';
  title: string;
  description: ReactNode;
  testId?: string;
};

/**
 * Every page-level empty state shares this layout: a gray icon tile, a headline, a secondary line, then
 * any actions passed as children. The fixed top offset (rather than vertical centering) keeps the headline
 * at the same height across pages whose content areas differ in height.
 */
export const NullState = ({
  className,
  description,
  headingAs: Heading = 'h1',
  icon: Icon,
  logo,
  testId,
  title,
  children,
}: PropsWithChildren<NullStateProps>) => {
  return (
    <section
      data-testid={EMPTY_BOARD}
      className={cn('flex w-full flex-col items-center px-8 pt-24 pb-16 text-center md:pt-32', className)}
    >
      <span className="flex size-12 shrink-0 items-center justify-center rounded-12 ads:bg-ads-gray-4 bg-gray-4 ads:text-icon-subtle text-gray-9">
        {Icon ? <Icon aria-hidden size="md" /> : logo}
      </span>

      <Heading
        className="mt-4 max-w-96 text-balance font-medium ads:text-primary text-16 text-gray-12"
        data-testid={testId}
      >
        {title}
      </Heading>

      <div className="mt-1 max-w-96 text-balance ads:text-metadata text-14 text-gray-10">{description}</div>

      {!!children && <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{children}</div>}
    </section>
  );
};
